import { useState, useMemo } from 'react'
import { Link as ExternalLink } from 'react-router-dom'

import QRCode from "react-qr-code"

import DataExportDialog from '/components/data-export-dialog'
import excelExport from './download-results-to-excel.js'
import ClickToCopy from '/components/click-to-copy'
import PassEmailCopyActions from '/components/pass-email-copy-actions'

import { Box, Button, IconButton, Dialog, DialogActions, DialogContent, Tooltip } from '@mui/material'

import {
  MRT_ToggleDensePaddingButton,
  MRT_ShowHideColumnsButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleGlobalFilterButton
} from 'material-react-table'

import SaveAltIcon from '@mui/icons-material/SaveAlt'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'

import appleButton from '/assets/images/add-to-apple-wallet.svg'
import googleButton from '/assets/images/add-to-google-wallet.svg'
import StudioTable, { useStudioTable } from '../studio-table'

const SHOW_SINGLE_PASS_INSTALL_DETAILS = false

const getErrorMessage = error => {
  if (typeof error === 'string') return error
  if (error?.message) return error.message
  if (error?.error?.message) return error.error.message
  return JSON.stringify(error)
}

const ResultsGrid = ({ template, passesToProcess, makePassResults, onIssueMore, progress = null }) => {

  const [selectedRow, setSelectedRow] = useState(null)
  const [dataExportDialogOpen, setDataExportDialogOpen] = useState(false)

  const showPassLinks = data => {
    setSelectedRow(data)
  }

  const processing = useMemo(() => makePassResults.length !== passesToProcess, [makePassResults, passesToProcess])
  const processedCount = progress?.processed ?? makePassResults.length
  const remainingCount = progress?.remaining ?? Math.max(0, passesToProcess - makePassResults.length)
  const failedCount = progress?.failed ?? makePassResults.filter(r => r.error).length

  const columns = useMemo(() => [
    {
      id: 'mailmergeName',
      exportKey: '_mailmerge.name',
      accessorFn: row => row['_mailmerge.name'],
      header: 'Recipient Name',
      filterVariant: 'autocomplete',
      size: 220,
    },
    {
      id: 'mailmergeEmail',
      exportKey: '_mailmerge.email',
      accessorFn: row => row['_mailmerge.email'],
      header: 'Recipient Email',
      filterVariant: 'autocomplete',
      size: 280,
    },
    {
      accessorKey: 'id',
      header: 'Pass ID',
      filterVariant: 'autocomplete',
      size: 300,
      Cell: ({ cell, row }) => row.original.error
        ? 'ERROR'
        : <ClickToCopy source={cell.getValue()} successMessage="Pass ID copied">
            <span className='text-[var(--linkColour)]'>{cell.getValue()}</span>
          </ClickToCopy>,
    },
    {
      accessorKey: 'error',
      header: 'Status',
      filterVariant: 'autocomplete',
      size: 260,
      Cell: ({ row }) => row.original.error
        ? getErrorMessage(row.original.error)
        : 'Created',
    },
    {
      accessorKey: 'installUrl',
      header: 'Install URL',
      filterVariant: 'autocomplete',
      size: 180,
      Cell: ({ cell }) => {
        const url = cell.getValue()
        return <ClickToCopy source={url} successMessage="Install URL copied">
          <span className='inline-block max-w-[220px] align-middle overflow-hidden text-ellipsis whitespace-nowrap' title={url}>{url}</span>
        </ClickToCopy>
      },
    },
    {
      accessorKey: 'appleUrl',
      header: 'Apple Install URL',
      filterVariant: 'autocomplete',
      size: 180,
      Cell: ({ cell }) => {
        const url = cell.getValue()
        return <ClickToCopy source={url} successMessage="Apple install URL copied">
          <span className='inline-block max-w-[220px] align-middle overflow-hidden text-ellipsis whitespace-nowrap' title={url}>{url}</span>
        </ClickToCopy>
      },
    },
    {
      accessorKey: 'googleUrl',
      header: 'Google Android Install URL',
      filterVariant: 'autocomplete',
      size: 260,
      Cell: ({ cell }) => {
        const url = cell.getValue()
        return <ClickToCopy source={url} successMessage="Google install URL copied">
          <span className='inline-block max-w-[220px] align-middle overflow-hidden text-ellipsis whitespace-nowrap' title={url}>{url}</span>
        </ClickToCopy>
      },
    },
    {
      id: 'view',
      header: 'View',
      size: 100,
      enableColumnFilter: false,
      Cell: ({ row }) => (
        <Button
          size='small'
          startIcon={<VisibilityOutlinedIcon />}
          onClick={event => {
            event.stopPropagation()
            showPassLinks(row.original)
          }}
        >
          View
        </Button>
      ),
    },
  ])

  const table = useStudioTable({
    tableKey: 'new-pass-results',
    columns,
    data: makePassResults,
    initialState: { showColumnFilters: false },
    enablePagination: false,
    enableBottomToolbar: false,
    enableRowSelection: false,
    enableMultiRowSelection: false,
    enableRowActions: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    positionToolbarAlertBanner: "none",
    getRowId: (originalRow) => originalRow.id,
    muiTableProps: { sx: { backgroundColor: 'transparent !important', width: '100%', margin: 0 } },
    muiTableBodyProps: { sx: { backgroundColor: 'transparent !important' } },
    muiTableBodyRowProps: ({ row }) => ({
      onDoubleClick: () => showPassLinks(row.original),
      sx: { backgroundColor: 'transparent !important', '&:hover': { backgroundColor: 'transparent !important' } }
    }),
    renderToolbarInternalActions: ({ table }) => (
      <Box>
        <MRT_ToggleGlobalFilterButton table={table} />
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ShowHideColumnsButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />
        <Tooltip title={processing ? "Export available when processing is complete" : "Export"}>
          <span>
            <IconButton onClick={() => setDataExportDialogOpen(true)} disabled={processing}><SaveAltIcon /></IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div className='flex flex-row items-center gap-4'>
        <div className='text-sm'>
          <b>Total:</b> {passesToProcess.toLocaleString()} &nbsp;
          <b>Processed:</b> {processedCount.toLocaleString()} &nbsp;
          <b>Left:</b> {remainingCount.toLocaleString()} &nbsp;
          <b>Failed:</b> {failedCount.toLocaleString()}
        </div>
      </div>
    )

  })

  return (
    <>
      <Box className='flex-1 min-h-0' sx={{ height: '100%', width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {SHOW_SINGLE_PASS_INSTALL_DETAILS && passesToProcess === 1 && makePassResults.length === 1
          ? <SinglePassResult pass={makePassResults[0]} />
          : <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
              <StudioTable table={table} />
            </Box>}
        <div className='flex justify-end pr-4 pb-4'>
          <Button variant='contained' onClick={onIssueMore}>Issue more passes</Button>
        </div>
      </Box>

      {selectedRow !== null &&
        <Dialog
          open={selectedRow !== null}
          onClose={() => setSelectedRow(null)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          slotProps={{
            paper: {
              sx: {
                width: '700px',
                maxWidth: '700px',
              },
            },
          }}
        >
          {selectedRow.error
            ? <DialogContent>
                <div className='flex flex-col gap-4'>
                  <div className='text-xl font-semibold'>Pass creation failed</div>
                  <div>{getErrorMessage(selectedRow.error)}</div>
                </div>
              </DialogContent>
            : <DialogContent>
              <div className='flex flex-row gap-5 justify-between'>

              <div className='flex flex-col gap-10 items-center '>
                <div><ExternalLink to={selectedRow?.appleUrl} target='_pass'><img src={appleButton} width={300} /></ExternalLink></div>
                <div className='bg-white p-5'>
                  <QRCode value={selectedRow?.appleUrl} />
                </div>
                <div>
                  <ClickToCopy source={selectedRow?.appleUrl} successMessage="Apple install URL copied">Copy URL to clipboard</ClickToCopy>
                </div>
              </div>

              <div className='flex flex-col gap-10 items-center'>
                <div><ExternalLink to={selectedRow?.googleUrl} target='_pass'><img src={googleButton} width={300} /></ExternalLink></div>
                <div className='bg-white p-5'>
                  <QRCode value={selectedRow?.googleUrl} />
                </div>
                <div>
                  <ClickToCopy source={selectedRow?.googleUrl} successMessage="Google install URL copied">Copy URL to clipboard</ClickToCopy>
                </div>
              </div>
              </div>
              <div className='mt-6'>
                <PassEmailCopyActions appleUrl={selectedRow?.appleUrl} googleUrl={selectedRow?.googleUrl} />
              </div>
            </DialogContent>}
          <DialogActions>
            <div className="flex flex-row w-full p-5">
              <Button variant="outlined" onClick={() => setSelectedRow(null)}>Close</Button>
            </div>
          </DialogActions>
        </Dialog>
      }

      <DataExportDialog open={dataExportDialogOpen} setOpen={setDataExportDialogOpen} fileName={`${template.template_id}-new-passes`} data={makePassResults} excelExport={excelExport} table={table} />

    </>
  )
}

const SinglePassResult = ({ pass }) => {
  if (pass.error) {
    const message = pass.error?.message ?? pass.error
    return (
      <Box className='flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center gap-4 p-5'>
        <div className='text-xl font-semibold'>Pass creation failed</div>
        <div>{message}</div>
      </Box>
    )
  }

  return (
    <Box className='flex-1 min-h-0 overflow-y-auto flex flex-col items-center justify-center gap-8 p-5'>
      <div className='text-xl font-semibold'>Pass created</div>
      <div className='flex flex-row gap-20 items-start justify-center flex-wrap'>
        <InstallResultPanel url={pass.appleUrl} buttonImage={appleButton} label='Apple URL' />
        <InstallResultPanel url={pass.googleUrl} buttonImage={googleButton} label='Google URL' />
      </div>
    <div className='text-lg flex flex-col items-center gap-2'>
      <div>Pass ID: <ClickToCopy source={pass.id}>{pass.id}</ClickToCopy></div>
      <div>Install URL: <ClickToCopy source={pass.installUrl}>{pass.installUrl}</ClickToCopy></div>
    </div>
    <PassEmailCopyActions appleUrl={pass.appleUrl} googleUrl={pass.googleUrl} />
  </Box>
)
}

const InstallResultPanel = ({ url, buttonImage, label }) => (
  <div className='flex flex-col gap-5 items-center'>
    <ExternalLink to={url} target='_pass'><img src={buttonImage} width={250} /></ExternalLink>
    <div className='bg-white p-5 rounded'>
      <QRCode size={256} value={url} viewBox='0 0 256 256' />
    </div>
    <div className='whitespace-nowrap'>
      <ClickToCopy source={url} successMessage={`${label} copied`}>Copy {label}</ClickToCopy>
    </div>
  </div>
)

export default ResultsGrid
